import React, { useEffect, useState, useCallback } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { FORMAT_LABELS, ALL_FORMATS } from '../../utils/censusFormatDetection';
import { FORMAT_TRANSFORMERS } from '../../utils/censusTransformers';

// Format status is persisted in localStorage for the MVP. The format
// definitions themselves live in code (/utils/censusFormatDetection.js +
// /utils/censusTransformers.js) and are managed via PR. Future iteration:
// move statusByKey into the system_config document so it's tenant-wide.
const STORAGE_KEY = 'hc-census-format-status-v1';

// Detection signatures in plain English -- so admins can see WHY a file gets
// classified a particular way, without having to read source code. Stays in
// sync with /utils/censusFormatDetection.js.
const FORMAT_SIGNATURES = {
  standard: {
    description: 'HealthCues Engine standard template. The fastest path: every field already in our internal model.',
    signatureColumns: ['Annual Salary', 'Filing Status', 'Worker Type'],
    sampleColumns: ['Employee ID', 'Name', 'Email', 'Annual Salary', 'State', 'Filing Status', 'Hire Date', 'Weekly Hours'],
  },
  healthcues: {
    description: 'HealthCues partner template. Used by partners producing per-pay-period gross wages and full IRS withholding fields.',
    signatureColumns: ['Gross Wages Per Pay Period', 'Federal Marital/Filing Status', 'Pay Schedule', 'Yearly'],
    sampleColumns: ['First Name', 'Last Name', 'Employee Id', 'Yearly', 'Pay Schedule', 'State', 'Federal Marital/Filing Status'],
  },
  adp_export: {
    description: 'ADP Workforce Now payroll export. Pre-tax earnings broken down by code (REG/OT/HOL/...) plus actual employer taxes paid.',
    signatureColumns: ['REG', 'OT', 'HOL', 'FUTAER', 'SSECER', 'MEDIER'],
    sampleColumns: ['Employee Number', 'REG', 'OT', 'OTP', 'HOL', 'NYSICK', 'WPPTO', 'FUTAER', 'MEDIER', 'SSECER'],
  },
  paycom_export: {
    description: 'Paycom payroll export — earnings split between Regular and Overtime with employee number.',
    signatureColumns: ['Regular', 'Overtime', 'Employee Number'],
    sampleColumns: ['Employee Number', 'First Name', 'Last Name', 'Regular', 'Overtime', 'Total Pay'],
  },
  paycor_export: {
    description: 'Paycor export — earnings codes REG/OT/PTO without ADP-specific employer-tax columns.',
    signatureColumns: ['REG', 'OT', 'PTO'],
    sampleColumns: ['Employee Number', 'Name', 'REG', 'OT', 'PTO', 'Total Pay'],
  },
  gusto_export: {
    description: 'Gusto export — snake_case columns including regular_hours / overtime_hours.',
    signatureColumns: ['regular_hours', 'overtime_hours'],
    sampleColumns: ['employee_id', 'employee_name', 'regular_hours', 'overtime_hours', 'regular_pay', 'overtime_pay'],
  },
  generic: {
    description: 'Generic spreadsheet (best-effort) — fallback when we can find name + salary-like columns but no payroll-system signature.',
    signatureColumns: ['Name', 'Salary'],
    sampleColumns: ['Name', 'Salary', 'State', 'Email'],
  },
};

const DEFAULT_STATUS = {
  standard: 'active',
  healthcues: 'active',
  adp_export: 'active',
  paycom_export: 'active',
  paycor_export: 'active',
  gusto_export: 'active',
  generic: 'active',
};

const STATUS_BADGES = {
  active: { color: C.green, bg: C.greenLight, label: 'Active' },
  inactive: { color: C.textMid, bg: C.bg, label: 'Inactive' },
  default: { color: '#0284c7', bg: '#e0f2fe', label: 'Active (default)' },
};

export default function CensusFormatMappingsEditor() {
  const [statusMap, setStatusMap] = useState(DEFAULT_STATUS);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setStatusMap({ ...DEFAULT_STATUS, ...parsed });
        }
      }
    } catch (err) {
      console.warn('Could not load census format status:', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const formats = ALL_FORMATS.filter((k) => k !== 'unknown');

  const handleToggle = (key) => {
    const current = statusMap[key] || 'active';
    const next = { ...statusMap, [key]: current === 'inactive' ? 'active' : 'inactive' };
    setStatusMap(next);
    setSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setSavedAt(new Date());
    } catch (err) {
      console.error('Failed to save format status:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.card} data-testid="admin-census-format-mappings">
      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ ...S.heading, marginBottom: 6 }}>Census Format Mappings</h3>
          <p style={{ color: C.textMid, fontSize: 13, margin: 0, maxWidth: 640 }}>
            The Smart Census Upload supports multiple payroll/partner formats. Each
            format has detection rules and a transformer that maps source columns
            into our internal employee model. Toggle a format off if you want it
            ignored (the file will fall back to <code>generic</code>).
          </p>
        </div>
        {saving && <span style={{ fontSize: 12, color: C.textMid }}>Saving…</span>}
        {!saving && savedAt && (
          <span style={{ fontSize: 12, color: C.green }}>Saved · {savedAt.toLocaleTimeString()}</span>
        )}
      </div>

      <div style={{
        border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Format</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Transformer</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {formats.map((key) => {
              const isExpanded = expanded === key;
              const status = statusMap[key] || 'active';
              const badge = key === 'standard'
                ? STATUS_BADGES.default
                : STATUS_BADGES[status] || STATUS_BADGES.active;
              const sig = FORMAT_SIGNATURES[key] || {};
              const transformer = FORMAT_TRANSFORMERS[key];
              return (
                <React.Fragment key={key}>
                  <tr style={{ borderBottom: `1px solid ${C.borderLight}` }}>
                    <td style={{ padding: '12px 14px', fontWeight: 600 }}>
                      {FORMAT_LABELS[key]}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px', borderRadius: 999,
                        background: badge.bg, color: badge.color,
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', color: C.textMid }}>
                      {transformer?.name || 'Built-in'}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <button
                        data-testid={`format-expand-${key}`}
                        onClick={() => setExpanded(isExpanded ? null : key)}
                        style={{
                          padding: '6px 10px', background: 'transparent',
                          color: C.blue, border: `1px solid ${C.border}`,
                          borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 8,
                        }}
                      >
                        {isExpanded ? 'Hide details' : 'View details'}
                      </button>
                      {key !== 'standard' && (
                        <button
                          data-testid={`format-toggle-${key}`}
                          onClick={() => handleToggle(key)}
                          style={{
                            padding: '6px 10px',
                            background: status === 'inactive' ? C.bg : '#fff',
                            color: status === 'inactive' ? C.textMid : C.text,
                            border: `1px solid ${C.border}`,
                            borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          {status === 'inactive' ? 'Activate' : 'Deactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={4} style={{ background: C.bg, padding: 16 }}>
                        <div style={{ fontSize: 13, color: C.textMid, marginBottom: 10 }}>
                          {sig.description}
                        </div>
                        <DetailGroup title="Detection signature columns" items={sig.signatureColumns || []} />
                        <DetailGroup title="Sample / mapped columns" items={sig.sampleColumns || []} />
                        <div style={{
                          marginTop: 12, padding: 10, background: '#fff', border: `1px solid ${C.border}`,
                          borderRadius: 6, fontSize: 12, color: C.textMid, lineHeight: 1.5,
                        }}>
                          <Icon name="information-circle-outline" style={{ fontSize: 14, color: C.blue, marginRight: 4 }} />
                          Detection rules and transformations are managed in code today. To request a new format, add a sample file plus the column patterns you've seen — engineering can extend the registry without breaking existing flows.
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailGroup({ title, items }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: C.textMid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.length === 0 ? (
          <span style={{ fontSize: 12, color: C.textLight }}>—</span>
        ) : items.map((it) => (
          <span key={it} style={{
            padding: '3px 8px', background: '#fff', border: `1px solid ${C.border}`,
            borderRadius: 4, fontSize: 11, fontFamily: 'monospace', color: C.text,
          }}>
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}
